/**
 * StepProgress - Accessible multi-step progress indicator
 * 
 * Design Principles:
 * - Semantic HTML (nav > ol > li)
 * - ARIA attributes for accessibility and styling
 * - Mobile-first responsive design
 * - CSS-driven state management via data attributes
 * 
 * Accessibility Features:
 * - Screen reader friendly with proper ARIA labels
 * - Current step indicated via aria-current="step"
 * - Keyboard navigable
 * - High contrast for visibility
 * 
 * Design Specs:
 * - Active step: bg-theme-700 border-theme-600 (tenant palette via --theme-*)
 * - Label: 14px (text-sm) at every breakpoint
 * - Smooth transitions for state changes
 */

import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export interface Step {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

export interface StepProgressProps {
  steps: Step[];
  currentStep: number; // 0-indexed
  className?: string;
}

export function StepProgress({ steps, currentStep, className = '' }: StepProgressProps) {
  // The connector runs circle-centre to circle-centre, measured from the DOM.
  // Each <li> sizes to its LABEL, not its circle, so no fixed inset lands on a
  // circle centre.
  const listRef = useRef<HTMLOListElement>(null);
  const [trackInset, setTrackInset] = useState<{ left: number; right: number } | null>(null);

  const measureTrack = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const circles = list.querySelectorAll<HTMLElement>('[data-step-circle]');
    const first = circles[0];
    const last = circles[circles.length - 1];
    if (!first || !last) return;
    const listBox = list.getBoundingClientRect();
    const firstBox = first.getBoundingClientRect();
    const lastBox = last.getBoundingClientRect();
    const left = firstBox.left + firstBox.width / 2 - listBox.left;
    const right = listBox.right - (lastBox.left + lastBox.width / 2);
    // Bail on an unchanged measurement: the ResizeObserver fires on any label reflow,
    // and a fresh object literal would re-render on numbers that did not move.
    setTrackInset((prev) =>
      prev && prev.left === left && prev.right === right ? prev : { left, right },
    );
  }, []);

  // Layout effect so the measured inset is applied before paint — no sliver frame.
  useLayoutEffect(() => {
    measureTrack();
    const list = listRef.current;
    if (!list) return;

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measureTrack);
      // Observe the ITEMS, not just the list: each <li> is label-width, and the
      // list stays full-width, so a label reflow (webfont swap, longer copy)
      // moves the circles without ever resizing the list.
      list.querySelectorAll('li').forEach((item) => observer?.observe(item));
    }

    // A webfont landing after first paint re-widths the labels; re-measure once
    // it settles, since that shifts every circle centre.
    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) measureTrack();
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [measureTrack, steps.length]);

  // Until measured, keep the track hidden rather than draw it at the wrong inset.
  const trackSpan = trackInset ? `calc(100% - ${trackInset.left + trackInset.right}px)` : '0px';
  
  // Calculate progress percentage for the animated line
  // Step 0: 25%, Step 1: 75%, Final Step: 100%
  const totalSteps = steps.length - 1; // Number of segments between steps
  
  let progressPercentage = 0;
  if (currentStep >= steps.length - 1) {
    // Last step - show 100%
    progressPercentage = 100;
  } else if (totalSteps > 0) {
    // Intermediate steps - add 25% to show progress on current step
    progressPercentage = (currentStep / totalSteps) * 100 + 25;
  } else {
    progressPercentage = 25;
  }
  
  return (
    <nav 
      aria-label="Progress" 
      className={className}
    >
      {/* Container with relative positioning for absolute line positioning */}
      <div className="relative">
        {/* Steps list */}
        <ol 
          ref={listRef}
          role="list" 
          className="relative flex items-start justify-between"
        >
          {/* Background line - positioned between first and last step circles */}
          <div 
            className="absolute h-2 bg-gray-200 top-5 sm:top-5 rounded-full"
            style={{
              left: trackInset?.left ?? 0,
              right: trackInset?.right ?? 0,
              visibility: trackInset ? 'visible' : 'hidden',
            }}
            aria-hidden="true"
          />
          
          {/* Animated progress line - fills from left to right with gradient fade.
              Three stops, no `color-mix`: an unsupported colour function invalidates
              the WHOLE background-image, so on iOS 15–16.1 (below Safari's 16.2
              color-mix support) a mid-stop would erase the bar rather than degrade it.
              Gradients interpolate premultiplied, so a solid → `transparent` ramp
              fades cleanly without the grey fringe `transparent` used to cause. */}
          <div
            className="absolute h-2 top-5 sm:top-5 transition-all duration-700 ease-out rounded-full"
            style={{
              left: trackInset?.left ?? 0,
              width: `calc(${trackSpan} * ${progressPercentage / 100})`,
              visibility: trackInset ? 'visible' : 'hidden',
              background:
                'linear-gradient(to right, var(--theme-600) 0%, var(--theme-600) 50%, transparent 100%)',
            }}
            aria-hidden="true"
          />
          {steps.map((step, index) => {
            const isComplete = index < currentStep;
            const isCurrent = index === currentStep;
            
            return (
              <li 
                key={step.id} 
                className="flex flex-col items-center"
              >
                {/* Step Circle */}
                <div
                  data-step-circle=""
                  data-status={
                    isComplete ? 'complete' : 
                    isCurrent ? 'current' : 
                    'upcoming'
                  }
                  aria-current={isCurrent ? 'step' : undefined}
                  className="
                    relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full 
                    transition-all duration-300 ease-in-out
                    sm:h-12 sm:w-12
                    
                    /* Current step - active state */
                    data-[status=current]:bg-theme-700
                    data-[status=current]:border-4
                    data-[status=current]:border-theme-600
                    data-[status=current]:text-white
                    data-[status=current]:shadow-lg
                    data-[status=current]:shadow-theme-600/30
                    
                    /* Completed step */
                    data-[status=complete]:bg-theme-700
                    data-[status=complete]:border-2
                    data-[status=complete]:border-theme-600
                    data-[status=complete]:text-white
                    
                    /* Upcoming step */
                    data-[status=upcoming]:bg-white
                    data-[status=upcoming]:border-2
                    data-[status=upcoming]:border-gray-200
                    data-[status=upcoming]:text-gray-400
                  "
                  role="img"
                  aria-label={
                    isComplete ? `${step.label} - Completed` :
                    isCurrent ? `${step.label} - Current step` :
                    `${step.label} - Upcoming`
                  }
                >
                  {step.icon ? (
                    <span className="flex h-5 w-5 items-center justify-center sm:h-6 sm:w-6">
                      {step.icon}
                    </span>
                  ) : (
                    <span className="text-sm font-semibold sm:text-base">
                      {isComplete ? (
                        /* Checkmark for completed */
                        <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </span>
                  )}
                </div>
                
                {/* Step Label */}
                <span
                  className="
                    mt-2 sm:mt-3 text-center text-sm font-medium transition-colors duration-300
                    whitespace-nowrap
                    
                    data-[status=current]:text-gray-900
                    data-[status=complete]:text-gray-900
                    data-[status=upcoming]:text-gray-400
                  "
                  data-status={
                    isComplete ? 'complete' : 
                    isCurrent ? 'current' : 
                    'upcoming'
                  }
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
